import { z } from "zod";
import { OpaqueIdSchema } from "./identity-schemas";
import {
  OpsDocAccessEntrySchema,
  OpsDocAccessUpdateSchema,
  OpsDocEmployeePrincipalSchema,
  OpsDocPageSchema,
  OpsDocPageSummarySchema,
  OpsDocSpaceSchema,
} from "./operations-docs-schemas";

export const OpsDocSpaceAccessUpdateSchema = OpsDocAccessUpdateSchema.safeExtend({
  accessMode: z.enum(["everyone", "restricted"]),
});
export type OpsDocSpaceAccessUpdate = z.infer<typeof OpsDocSpaceAccessUpdateSchema>;
export const OpsDocSpaceAccessResponseSchema = z.strictObject({
  space: OpsDocSpaceSchema,
  entries: z.array(OpsDocAccessEntrySchema).max(200),
});
export const OpsDocOwnersResponseSchema = z.strictObject({
  employees: z.array(OpsDocEmployeePrincipalSchema).max(500),
});
export const OpsDocOwnerUpdateSchema = z.strictObject({
  expectedRevision: z.number().int().positive(),
  ownerEmployeeId: OpaqueIdSchema,
});
export const OpsDocOwnerResponseSchema = z.strictObject({ page: OpsDocPageSchema.nullable() });
export const OpsDocVerificationUpdateSchema = z
  .strictObject({
    expectedRevision: z.number().int().positive(),
    verifiedAt: z.iso.date().nullable(),
    nextVerificationAt: z.iso.date().nullable(),
    confirmCurrentVersion: z.boolean(),
  })
  .refine(
    (value) =>
      !value.verifiedAt ||
      !value.nextVerificationAt ||
      value.nextVerificationAt >= value.verifiedAt,
    {
      message: "Next verification must be on or after the verification date.",
      path: ["nextVerificationAt"],
    },
  )
  .refine((value) => !value.confirmCurrentVersion || value.verifiedAt !== null, {
    message: "Choose a verification date.",
    path: ["verifiedAt"],
  });
export type OpsDocVerificationUpdate = z.infer<typeof OpsDocVerificationUpdateSchema>;

export const OpsDocReviewPageSchema = OpsDocPageSummarySchema.extend({
  ownerEmployeeId: OpaqueIdSchema,
  ownerDisplayName: z.string().min(1).max(120),
  spaceName: z.string().min(1).max(120),
});
export type OpsDocReviewPage = z.infer<typeof OpsDocReviewPageSchema>;
export const OpsDocReviewsResponseSchema = z.strictObject({
  pages: z.array(OpsDocReviewPageSchema).max(1_000),
});

export function docVerificationState(
  page: {
    verifiedAt: string | null;
    nextVerificationAt: string | null;
    verificationStale: boolean;
  },
  today = new Date().toISOString().slice(0, 10),
): "Not verified" | "Verified" | "Needs review" {
  if (
    page.verificationStale ||
    (page.nextVerificationAt !== null && page.nextVerificationAt <= today)
  )
    return "Needs review";
  return page.verifiedAt ? "Verified" : "Not verified";
}

export const DOC_IMAGE_MAX_BYTES = 5 * 1024 * 1024;
export const DOC_IMAGE_MAX_COUNT = 200;
export const DOC_IMAGE_TYPES = ["image/png", "image/jpeg", "image/webp", "image/avif"] as const;
export const OpsDocImageSchema = z.strictObject({
  imageId: OpaqueIdSchema,
  url: z.string().max(300),
});
export function docImageUrl(pageId: string, imageId: string): string {
  return `/api/ops/docs/pages/${OpaqueIdSchema.parse(pageId)}/images/${OpaqueIdSchema.parse(imageId)}`;
}
export function safeDocImageUrl(
  value: string | undefined,
  pageId: string | undefined,
): string | null {
  if (!value || !pageId) return null;
  const match = /^\/api\/ops\/docs\/pages\/([a-zA-Z0-9_-]+)\/images\/([a-zA-Z0-9_-]+)$/.exec(value);
  return match?.[1] === pageId && OpaqueIdSchema.safeParse(match[2]).success ? value : null;
}
